// src/components/Lms/Pages/CourseCurriculumReviewView.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import "../Styles/LmsExtra.css";
import {
    FiArrowLeft,
    FiChevronDown,
    FiChevronUp,
    FiFileText,
    FiClipboard,
    FiCheckCircle,
    FiEye,
} from "react-icons/fi";

import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { cursosNormalize } from "../Objetos/CursosModel";
import { moduloNormalize } from "../Objetos/ModuloModel";
import { leccionesNormalize } from "../Objetos/LeccionesModel";

const CourseCurriculumReviewView = () => {
    const { idCurso } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // ---------- State principal ----------
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedModules, setExpandedModules] = useState({});
    const [carreraId, setCarreraId] = useState(null);

    useEffect(() => {
        const fetchCarreraId = async () => {
            if (!idCurso) return;
            
            // Check location state first
            const stateCarreraId = location.state?.fromCarreraId;
            if (stateCarreraId) {
                setCarreraId(Number(stateCarreraId));
                sessionStorage.setItem(`lms_last_carrera_id_${idCurso}`, stateCarreraId);
                return;
            }

            // Check sessionStorage next
            const sessionCarreraId = sessionStorage.getItem(`lms_last_carrera_id_${idCurso}`);
            if (sessionCarreraId) {
                setCarreraId(Number(sessionCarreraId));
                return;
            }

            try {
                const resRel = await axios.get(LMS_ENDPOINTS.semestreCurso);
                const relaciones = (resRel.data || []).map(Models.semestreCurso);
                const relacion = relaciones.find(r => Number(r.idCurso) === Number(idCurso) && r.activo);
                
                if (relacion) {
                    const idSem = relacion.idSemestre;
                    const resSem = await axios.get(LMS_ENDPOINTS.semestres);
                    const semestres = (resSem.data || []).map(Models.semestre);
                    const semestre = semestres.find(s => Number(s.idSemestre) === Number(idSem) && s.activo);
                    
                    if (semestre) {
                        setCarreraId(semestre.idCarrera);
                        sessionStorage.setItem(`lms_last_carrera_id_${idCurso}`, semestre.idCarrera);
                    }
                }
            } catch (err) {
                console.error("Error al obtener idCarrera para el curso:", err);
            }
        };
        fetchCarreraId();
    }, [idCurso, location.state]);

    // ---------- Función para cargar curso completo desde BD ----------
    const loadCursoCompleto = async () => {
        if (!idCurso) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const [respCurso, respModulos, respLecciones, respCuestionarios, respExamenes, respModuloContenido] = await Promise.all([
                axios.get(LMS_ENDPOINTS.cursosById(idCurso)),
                axios.get(LMS_ENDPOINTS.moduloByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.leccionesByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.cuestionarioByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.examenByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(idCurso))
            ]);

            const cursoDataRaw = respCurso.data;
            const cursoData = Array.isArray(cursoDataRaw) ? cursoDataRaw[0] : cursoDataRaw;

            if (!cursoData) {
                setLoading(false);
                return;
            }
            const cursoEncontrado = cursosNormalize(cursoData);

            const modulosDelCurso = (respModulos.data || [])
                .map(moduloNormalize)
                .sort((a, b) => (a.orden || 0) - (b.orden || 0));

            const todasLecciones = (respLecciones.data || []).map(leccionesNormalize);
            const todosCuestionarios = respCuestionarios.data || [];
            const todosExamenes = respExamenes.data || [];
            const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

            const modulosMapeados = modulosDelCurso.map((mod) => {
                const contenidosDelModulo = todoModuloContenido
                    .filter((mc) => mc.idModulo === mod.idModulo && mc.activo)
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0));

                const contenidoMapeado = contenidosDelModulo.map((mc) => {
                    const tipo = mc.tablaContenido;

                    if (tipo === "Lecciones") {
                        const leccion = todasLecciones.find(l => l.idLeccion === mc.idAsociado);
                        if (!leccion) return null;
                        return { tipo: "LECCION", id: leccion.idLeccion, titulo: leccion.titulo, activo: leccion.activo };
                    }

                    if (tipo === "CUESTIONARIO") {
                        const cuestionario = todosCuestionarios.find(q => (q.idCuestionario || q.IdCuestionario) === mc.idAsociado);
                        if (!cuestionario) return null;
                        return { tipo: "CUESTIONARIO", id: cuestionario.idCuestionario || cuestionario.IdCuestionario, titulo: cuestionario.nombre || cuestionario.Nombre, activo: cuestionario.activo ?? cuestionario.Activo };
                    }

                    if (tipo === "Examen") {
                        const examen = todosExamenes.find(e => (e.idExamen || e.IdExamen) === mc.idAsociado);
                        if (!examen) return null;
                        return { tipo: "EXAMEN", id: examen.idExamen || examen.IdExamen, titulo: examen.nombre || examen.Nombre, activo: examen.activo ?? examen.Activo };
                    }
                    return null;
                }).filter(Boolean);

                return { id: mod.idModulo, titulo: mod.nombre, activo: mod.activo, contenido: contenidoMapeado };
            });

            setCourse({ ...cursoEncontrado, titulo: cursoEncontrado.nombre, modulos: modulosMapeados });
            setError(null);
        } catch (err) {
            console.error("Error al cargar curso en modo vista:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCursoCompleto();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idCurso]);

    useEffect(() => {
        if (!course) return;
        const next = {};
        (course.modulos || []).forEach((m) => { next[m.id] = true; });
        setExpandedModules(next);
    }, [course]);

    const toggleModule = (moduleId) => setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));

    if (loading) return <div className="p-8 text-center text-gray-500">Cargando curso...</div>;
    if (error || !course) return <div className="p-8 text-center text-red-500">Error al cargar el curso. <button onClick={() => {
        if (carreraId) {
            navigate(`/menu/lms/carrera-curriculo-revision/${carreraId}`);
        } else {
            navigate(-1);
        }
    }} className="mt-4 text-blue-600 underline">Volver</button></div>;

    return (
        <div className="cc-page">
            <div className="cc-breadcrumb">
                <button type="button" className="cc-breadcrumb-back" onClick={() => {
                    if (carreraId) {
                        navigate(`/menu/lms/carrera-curriculo-revision/${carreraId}`);
                    } else {
                        navigate(-1);
                    }
                }}>
                    <FiArrowLeft /> Volver
                </button>
            </div>

            <div className="cc-layout">
                <div className="cc-main">
                    <h1 className="cc-course-title">{course.titulo}</h1>

                    <div className="cc-chip-row">
                        <span className="cc-chip">{course.modulos?.length || 0} módulos</span>
                        <span className="cc-chip">{course.modulos?.reduce((acc, m) => acc + (m.contenido?.filter(c => c.tipo === 'LECCION').length || 0), 0)} lecciones</span>
                    </div>

                    <div className="cc-toolbar">
                        <button type="button" className="cc-add-lesson-btn" onClick={() => {
                            const allExpanded = Object.values(expandedModules).every(v => v);
                            const next = {};
                            (course.modulos || []).forEach(m => { next[m.id] = !allExpanded; });
                            setExpandedModules(next);
                        }}>
                            {Object.values(expandedModules).every(v => v) ? "Contraer todas" : "Expandir todas"}
                        </button>
                    </div>

                    <div className="cc-modules-list">
                        {(course.modulos || []).map((modulo, index) => (
                            <div key={modulo.id} className="cc-module-card">
                                <div className="cc-module-header">
                                    <div className="cc-module-header-left">
                                        <div className="cc-module-index-badge">{index + 1}</div>
                                        <div>
                                            <h2 className="cc-module-title">{modulo.titulo}</h2>
                                            <p className="cc-module-meta">{modulo.contenido?.length || 0} elementos</p>
                                        </div>
                                    </div>
                                    <div className="cc-module-header-right">
                                        <button type="button" className="cc-module-toggle-btn" onClick={() => toggleModule(modulo.id)}>
                                            {expandedModules[modulo.id] ? <FiChevronUp /> : <FiChevronDown />}
                                        </button>
                                    </div>
                                </div>

                                {expandedModules[modulo.id] && (
                                    <div className="cc-lessons-list">
                                        {(modulo.contenido || []).map((item) => {
                                            const Icon = item.tipo === "LECCION" ? FiFileText : item.tipo === "CUESTIONARIO" ? FiClipboard : FiCheckCircle;
                                            return (
                                                <div key={`${item.tipo}-${item.id}`} className="cc-lesson-row">
                                                    <div className="cc-lesson-main">
                                                        <div className="cc-lesson-icon"><Icon /></div>
                                                        <div className="cc-lesson-texts">
                                                            <h3 className="cc-lesson-title">{item.titulo}</h3>
                                                        </div>
                                                    </div>
                                                    <div className="cc-lesson-actions">
                                                        <button type="button" className="cc-lesson-btn cc-lesson-btn-ghost"
                                                            onClick={() => navigate(`/menu/lms/curso-player-revision-view/${idCurso}/${modulo.id}/${item.id}/${item.tipo}`, { state: { fromCarreraId: carreraId } })}>
                                                            <FiEye /> Ver
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <aside className="cc-sidebar">
                    <div className="cc-summary-card">
                        <h3 className="cc-summary-title">Resumen</h3>
                        <div className="cc-summary-grid">
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">Módulos</small>
                                <strong className="cc-summary-value">{course.modulos?.length || 0}</strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">Lecciones</small>
                                <strong className="cc-summary-value">{course.modulos?.reduce((acc, m) => acc + (m.contenido?.filter(c => c.tipo === 'LECCION').length || 0), 0)}</strong>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default CourseCurriculumReviewView;
